import { Link } from 'react-router-dom';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'light' | 'dark';
  showText?: boolean;
  linkTo?: string;
  className?: string;
}

// Pot icon with steam - matches pencil.dev design
function PotIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 56 56" fill="none" className={className}>
      {/* Steam bubbles */}
      <ellipse cx="18.5" cy="11" rx="2.5" ry="5" fill="currentColor" opacity="0.9" />
      <ellipse cx="28" cy="10" rx="3" ry="6" fill="currentColor" opacity="0.9" />
      <ellipse cx="37.5" cy="12" rx="2.5" ry="5" fill="currentColor" opacity="0.9" />
      {/* Pot rim */}
      <rect x="8" y="18" width="40" height="5" rx="2" fill="currentColor" />
      {/* Pot body */}
      <path
        d="M 12 22 L 44 22 L 44 38 Q 44 44 38 44 L 18 44 Q 12 44 12 38 Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function Logo({
  size = 'md',
  variant = 'light',
  showText = true,
  linkTo,
  className = '',
}: LogoProps) {
  const sizes = {
    sm: { icon: 'w-8 h-8', text: 'text-lg', gap: 'gap-2' },
    md: { icon: 'w-9 h-9', text: 'text-xl', gap: 'gap-2.5' },
    lg: { icon: 'w-12 h-12', text: 'text-2xl', gap: 'gap-3' },
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const textColor = variant === 'light' ? 'text-white' : 'text-slate-900';

  const content = (
    <div className={`flex items-center ${sizes[size].gap} ${className}`}>
      {/* Icon container with gradient background */}
      <div
        className={`${sizes[size].icon} rounded-lg flex items-center justify-center`}
        style={{
          background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
        }}
      >
        <PotIcon className={`${iconSizes[size]} text-white`} />
      </div>
      {showText && (
        <span className={`${sizes[size].text} font-bold tracking-wide ${textColor}`}>
          PLEKO
        </span>
      )}
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="flex items-center">
        {content}
      </Link>
    );
  }

  return content;
}

// Compact logo for small spaces (just the icon)
export function LogoIcon({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { container: 'w-8 h-8', icon: 'w-4 h-4' },
    md: { container: 'w-9 h-9', icon: 'w-5 h-5' },
    lg: { container: 'w-12 h-12', icon: 'w-6 h-6' },
  };

  return (
    <div
      className={`${sizes[size].container} rounded-lg flex items-center justify-center`}
      style={{
        background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
      }}
    >
      <PotIcon className={`${sizes[size].icon} text-white`} />
    </div>
  );
}
